import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { createAction, createReducer, createSelector, on, props } from "@ngrx/store";
import { catchError, map, mergeMap, of, take } from "rxjs";
import { ICreateFiscalYearRequest, IFiscalYear, ILockFiscalYearRequest, IUnlockFiscalYearRequest, IUpdateFiscalYearRequest } from "src/app/setting/models/fiscal-year";
import { FiscalYearService } from "src/app/setting/services/fiscal-year.service";
import { convertDatesInEntity } from "src/app/shared/helpers/date-helpers";
import { AppStateInterface } from "../appState.interface";

export interface FiscalYearStateInterface {
  isLoading: boolean,
  fiscalYears: IFiscalYear[],
  error: string | null
}

// actions

export const getFiscalYears = createAction('[FiscalYears] Get FiscalYears')
export const getFiscalYearsSuccess = createAction('[FiscalYears] Get FiscalYears success', props<{fiscalYears: IFiscalYear[]}>())
export const getFiscalYearsFailure = createAction('[FiscalYears] Get FiscalYears failure', props<{error: string}>())

export const createFiscalYear = createAction('[FiscalYears] Create FiscalYears', props<{request: ICreateFiscalYearRequest}>())
export const createFiscalYearSuccess = createAction('[FiscalYears] Create FiscalYears success', props<{fiscalYear: IFiscalYear}>())
export const createFiscalYearFailure = createAction('[FiscalYears] Create FiscalYears failure', props<{error: string}>())

export const updateFiscalYear = createAction('[FiscalYears] Update FiscalYears', props<{request: IUpdateFiscalYearRequest}>())
export const updateFiscalYearSuccess = createAction('[FiscalYears] Update FiscalYears success', props<{fiscalYear: IFiscalYear}>())
export const updateFiscalYearFailure = createAction('[FiscalYears] Update FiscalYears failure', props<{error: string}>())

export const lockFiscalYear = createAction('[FiscalYears] Lock FiscalYears', props<{request: ILockFiscalYearRequest}>())
export const lockFiscalYearSuccess = createAction('[FiscalYears] Lock FiscalYears success', props<{fiscalYear: IFiscalYear}>())
export const lockFiscalYearFailure = createAction('[FiscalYears] Lock FiscalYears failure', props<{error: string}>())

export const unlockFiscalYear = createAction('[FiscalYears] Unlock FiscalYears', props<{request: IUnlockFiscalYearRequest}>())
export const unlockFiscalYearSuccess = createAction('[FiscalYears] Unlock FiscalYears success', props<{fiscalYear: IFiscalYear}>())
export const unlockFiscalYearFailure = createAction('[FiscalYears] Unlock FiscalYears failure', props<{error: string}>())

export const deleteFiscalYear = createAction('[FiscalYears] Delete FiscalYears', props<{id: string}>())
export const deleteFiscalYearSuccess = createAction('[FiscalYears] Delete FiscalYears success', props<{id: string}>())
export const deleteFiscalYearFailure = createAction('[FiscalYears] Delete FiscalYears failure', props<{error: string}>())

// states
export const initialState: FiscalYearStateInterface = {
  isLoading: false,
  fiscalYears: [],
  error: null
}

// reducers
export const fiscalYearsReducers = createReducer(
  initialState,
  on(getFiscalYears, (state) => ({...state, isLoading: true})),
  on(getFiscalYearsSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears: action.fiscalYears
  })),
  on(getFiscalYearsFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  //create
  on(createFiscalYear, (state) => ({...state, isLoading: true})),
  on(createFiscalYearSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears:[...state.fiscalYears, action.fiscalYear]
  })),
  on(createFiscalYearFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  // update
  on(updateFiscalYear, (state) => ({...state, isLoading: true})),
  on(updateFiscalYearSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears: state.fiscalYears.map(fiscalYear => fiscalYear.id === action.fiscalYear.id ? action.fiscalYear : fiscalYear)
  })),
  on(updateFiscalYearFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  // lock
  on(lockFiscalYear, (state) => ({...state, isLoading: true})),
  on(lockFiscalYearSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears: state.fiscalYears.map(fiscalYear => fiscalYear.id === action.fiscalYear.id ? action.fiscalYear : fiscalYear)
  })),
  on(lockFiscalYearFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  // unlock
  on(unlockFiscalYear, (state) => ({...state, isLoading: true})),
  on(unlockFiscalYearSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears: state.fiscalYears.map(fiscalYear => fiscalYear.id === action.fiscalYear.id ? action.fiscalYear : fiscalYear)
  })),
  on(unlockFiscalYearFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  //delete
  on(deleteFiscalYear, (state) => ({...state, isLoading: true})),
  on(deleteFiscalYearSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    fiscalYears: state.fiscalYears.filter(costCenter =>
      costCenter.id !== action.id),
  })),
  on(deleteFiscalYearFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  }))
)

export const selectFeature = (state: AppStateInterface) => state.fiscalYears

// Selectors
export const isLoadingSelector = createSelector(selectFeature, (state) => state.isLoading);

export const fiscalYearsSelector = createSelector(selectFeature, (state) => state.fiscalYears); 

export const errorSelector = createSelector(selectFeature, (state) => state.error); 

// effects


@Injectable()
export class FiscalYearsEffects {
  getFiscalYears$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(getFiscalYears),
        mergeMap(() =>{
          return this.fiscalYearService
            .searchFiscalYears({pageNumber: 1, pageSize: 999999})
            .pipe(
              map((response) => getFiscalYearsSuccess({
                fiscalYears: response.data.map(x => convertDatesInEntity(x))
              })),
              catchError((error) => of(getFiscalYearsFailure({error: error})))
            );
          }),
        take(1) // makes sure this the api call gets executed once
      )
  });

  createFiscalYear$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(createFiscalYear),
        mergeMap(({request}) =>{
          return this.fiscalYearService.createFiscalYear(request)
            .pipe(
              map((fiscalYear) => createFiscalYearSuccess({
                fiscalYear: convertDatesInEntity(fiscalYear)
              })),
              catchError((error) => of(createFiscalYearFailure({error: error})))
            );
          }),
      )
  });

  updateFiscalYear$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(updateFiscalYear),
        mergeMap(({request}) =>{
          return this.fiscalYearService
            .updateFiscalYear(request)
            .pipe(
              map((fiscalYear) => updateFiscalYearSuccess({
                fiscalYear: convertDatesInEntity(fiscalYear)
              })),
              catchError((error) => of(updateFiscalYearFailure({error: error})))
            );
          }),
      )
  });

  lockFiscalYear$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(lockFiscalYear),
        mergeMap(({request}) =>{
          return this.fiscalYearService
            .lockFiscalYear(request)
            .pipe(
              map((fiscalYear) => lockFiscalYearSuccess({
                fiscalYear: convertDatesInEntity(fiscalYear)
              })),
              catchError((error) => of(lockFiscalYearFailure({error: error})))
            );
          }),
      )
  });

  unlockFiscalYear$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(unlockFiscalYear),
        mergeMap(({request}) =>{
          return this.fiscalYearService
            .unlockFiscalYear(request)
            .pipe(
              map((fiscalYear) => unlockFiscalYearSuccess({
                fiscalYear: convertDatesInEntity(fiscalYear)
              })),
              catchError((error) => of(unlockFiscalYearFailure({error: error})))
            );
          }),
      )
  });

  // deleteFiscalYear$ = createEffect(() => {
  //   return this.actions$.pipe(
  //       ofType(deleteFiscalYear),
  //       mergeMap(({id}) =>{
  //         return this.fiscalYearService
  //           .deleteFiscalYear(id)
  //           .pipe(
  //             map((response) => deleteFiscalYearSuccess({
  //               id: id
  //             })),
  //             catchError((error) => of(deleteFiscalYearFailure({error: error})))
  //           );
  //         }),
  //     )
  // });

  constructor(
    private actions$: Actions,
    private fiscalYearService: FiscalYearService
  ) {
    
  }
}